import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    TextField,
    Button,
    Tooltip,
    Typography,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    InputAdornment,
    Chip,
    Badge,
    Alert,
    Autocomplete,
    IconButton,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    CalendarToday as CalendarIcon,
    Business as BusinessIcon,
    Refresh as RefreshIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import FilterListAltIcon from '@mui/icons-material/FilterListAlt';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import DeleteConfirmDialog from '../../../../componentsUiBlock/DeleteConfirmDialog';
import { request } from '../../../../utils/request';
import { useDebounce } from '../../../../utils/useDebounce';
import { toast } from 'react-toastify';
import { FilterParams, ProductReturn, Status } from '../../types';

// Status configuration
const STATUS_OPTIONS: Status[] = [
    'RETURN_CREATED',
    'RECEIVED_AT_SHOP',
    'SENT_TO_VENDOR',
    'RECEIVED_BY_VENDOR'
];

const STATUS_CONFIG: Record<Status, { label: string; color: string; icon: string }> = {
    RETURN_CREATED: { label: 'Return Created', color: 'info', icon: '🟢' },
    RECEIVED_AT_SHOP: { label: 'Received at Shop', color: 'primary', icon: '🟡' },
    SENT_TO_VENDOR: { label: 'Sent to Vendor', color: 'warning', icon: '🟣' },
    RECEIVED_BY_VENDOR: { label: 'Received by Vendor', color: 'info', icon: '⚫' }
};

export const getStatusIconBall = (status?: string): string => {
    switch (status) {
        case 'RETURN_CREATED': return '🟢';
        case 'RECEIVED_AT_SHOP': return '🟡';
        case 'SENT_TO_VENDOR': return '🟣';
        case 'RECEIVED_BY_VENDOR': return '⚫';
        default: return '🟢';
    }
};

export const getStatusColor = (status?: string): string => {
    switch (status) {
        case 'RETURN_CREATED': return 'success';
        case 'RECEIVED_AT_SHOP': return 'warning';
        case 'SENT_TO_VENDOR': return 'primary';
        case 'RECEIVED_BY_VENDOR': return 'info';
        default: return 'default';
    }
};

const ListProductReturn = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // State for product returns
    const [productReturns, setProductReturns] = useState<ProductReturn[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // State for filter data
    const [parties, setParties] = useState<any[]>([]);
    const [vendors, setVendors] = useState<any[]>([]);
    const [partyFilter, setPartyFilter] = useState<any | null>(null);
    const [vendorFilter, setVendorFilter] = useState<any | null>(null);

    // Filter states
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null,
    });
    const [quickDateFilter, setQuickDateFilter] = useState<string>('ALL');
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Sorting and pagination
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<string>('productReturnDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [, setTotalPages] = useState(0);

    // Delete dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ProductReturn | null>(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Check location state for pre-set filters
    const checkLocationState = useCallback(() => {
        const state = location.state as {
            partyId?: string;
            vendorId?: string;
            source?: string;
            status?: string;
            startDate?: string;
            endDate?: any;
        };
        return {
            partyId: state?.partyId,
            vendorId: state?.vendorId,
            source: state?.source,
            status: state?.status,
            startDate: state?.startDate,
            endDate: state?.endDate
        };
    }, [location.state]);

    // Apply filters from location state
    const applyLocationFilters = useCallback(() => {
        const locationState = checkLocationState();

        if (locationState.source === 'dashboard' && !filtersApplied) {
            // Apply status filter
            if (locationState.status && locationState.status !== 'ALL') {
                setStatusFilter(locationState.status);
            }

            // Apply date range filter
            // if (locationState.startDate && locationState.endDate) {
            //     try {
            //         const start = parseISO(locationState.startDate);
            //         const end = parseISO(locationState.endDate);
            //         setDateRange({ start, end });
            //         setQuickDateFilter('ALL');
            //     } catch (error) {
            //         console.error('Error parsing dates:', error);
            //     }
            // }

            setFiltersApplied(true);
        }
    }, [checkLocationState, filtersApplied]);

    // Build query params from filters
    const buildQueryParams = useCallback((): FilterParams => {
        const params: FilterParams = {
            page: page + 1,
            limit: rowsPerPage,
            sortBy: orderBy,
            sortOrder: order,
            dateRange: ''
        };

        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== 'ALL') params.status = statusFilter;
        if (partyFilter?._id) params.partyId = partyFilter._id;
        if (vendorFilter?._id) params.vendorId = vendorFilter._id;

        // Handle date filters
        if (dateRange.start || dateRange.end) {
            if (dateRange.start) params.startDate = format(dateRange.start, 'yyyy-MM-dd');
            if (dateRange.end) params.endDate = format(dateRange.end, 'yyyy-MM-dd');
        } else if (quickDateFilter !== 'ALL') {
            params.dateRange = quickDateFilter;
        }

        return params;
    }, [debouncedSearch, statusFilter, partyFilter, vendorFilter, dateRange, quickDateFilter, page, rowsPerPage, orderBy, order]);

    // Fetch filter data (parties and vendors)
    const fetchFilterData = useCallback(async () => {
        try {
            // Fetch parties
            const partiesResponse = await request({
                url: 'party/get-all-parties',
                method: 'get',
            });
            const partiesData = partiesResponse.parties || [];
            setParties(partiesData);

            // Fetch vendors
            const vendorsResponse = await request({
                url: 'vendor/get-all-vendors',
                method: 'get',
            });
            const vendorsData = vendorsResponse.vendors || [];
            setVendors(vendorsData);

            // Check location state and set filters
            const locationState = checkLocationState();

            if (locationState.partyId) {
                const foundParty = partiesData.find((p: any) => p._id === locationState.partyId);
                if (foundParty) {
                    setPartyFilter(foundParty);
                }
            }

            if (locationState.vendorId) {
                const foundVendor = vendorsData.find((v: any) => v._id === locationState.vendorId);
                if (foundVendor) {
                    setVendorFilter(foundVendor);
                }
            }
            applyLocationFilters()
        } catch (error) {
            console.error('Error fetching filter data:', error);
        }
    }, [checkLocationState, applyLocationFilters]);

    // Fetch product returns with filters
    const fetchProductReturns = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildQueryParams();
            const queryString = new URLSearchParams(params as any).toString();

            const response = await request({
                url: `productReturn/get-all-product-returns?${queryString}`,
                method: 'get',
            });

            if (response.success) {
                setProductReturns(response.data || []);
                if (response.pagination) {
                    setTotal(response.pagination.total);
                    setTotalPages(response.pagination.totalPages);
                } else {
                    setTotal(response.data?.length || 0);
                }
            }
        } catch (error) {
            console.error('Error fetching product returns:', error);
            toast.error('Failed to fetch product returns');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [buildQueryParams]);

    // Fetch data on component mount
    useEffect(() => {
        fetchFilterData();
    }, [fetchFilterData]);

    // Fetch product returns when filters change
    useEffect(() => {
        fetchProductReturns();
    }, [fetchProductReturns]);

    // Clear filters and location state
    const handleClearFilters = () => {
        setSearch('');
        setStatusFilter('ALL');
        setPartyFilter(null);
        setVendorFilter(null);
        setDateRange({ start: null, end: null });
        setQuickDateFilter('ALL');
        setPage(0);

        // Clear location state by navigating without state
        navigate('/productReturn', { replace: true });
    };

    // Helper functions
    const getPartyName = (partyId?: any) => {
        if (!partyId) return '-';
        if (typeof partyId === 'object' && partyId.partyName) return partyId.partyName;
        const party = parties.find(p => p._id === partyId);
        return party ? party.partyName : 'Unknown Party';
    };

    const getVendorName = (vendorId?: any) => {
        if (!vendorId) return '-';
        if (typeof vendorId === 'object' && vendorId.vendorName) return vendorId.vendorName;
        const vendor = vendors.find(v => v._id === vendorId);
        return vendor ? vendor.vendorName : 'Unknown Vendor';
    };

    const formatDate = (date: any) => {
        if (!date) return '-';
        return format(new Date(date), 'dd/MM/yyyy');
    };

    // Event handlers
    const handleSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchProductReturns();
    };

    const handleDeleteClick = (pr: ProductReturn) => {
        setItemToDelete(pr);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        setLoadingDelete(true);
        try {
            const res = await request({
                url: `productReturn/delete-product-return/${itemToDelete._id}`,
                method: 'delete',
            });

            if (res.success) {
                toast.success(res.message || 'Product Return Deleted');
                await fetchProductReturns();
                setOpenDeleteDialog(false);
                setItemToDelete(null);
            }
        } catch (error: any) {
            console.error('Error deleting product return:', error);
            toast.error(error);
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
        setItemToDelete(null);
    };

    const headCells = [
        { id: 'productReturnNumber', label: 'Return Number', sortable: true, width: 60 },
        { id: 'productReturnDate', label: 'Return Date', sortable: true, width: 90 },
        { id: 'items', label: 'Items', sortable: false, width: 200 },
        { id: 'party', label: 'From Party', sortable: false, width: 120 },
        { id: 'vendor', label: 'To Vendor', sortable: false, width: 120 },
        { id: 'status', label: 'Status', sortable: true, width: 120 },
        { id: 'action', label: 'Actions', sortable: false, width: 240 },
    ];

    // Check if any filters are active
    const hasActiveFilters = Boolean(
        search ||
        statusFilter !== 'ALL' ||
        partyFilter ||
        vendorFilter ||
        dateRange.start ||
        dateRange.end ||
        quickDateFilter !== 'ALL'
    );

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Card elevation={2} sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ pb: 2 }}>
                        {/* Header with filters */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate("/productReturn/createProductReturn")}
                                startIcon={<AddIcon />}
                                size="small"
                            >
                                New Product Return
                            </Button>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                {/* Total count */}
                                <Badge
                                    badgeContent={total}
                                    showZero
                                    color="primary"
                                    sx={{ ml: 'auto' }}
                                >
                                    <AssignmentReturnedIcon color="action" />
                                </Badge>

                                {/* Search */}
                                <TextField
                                    size="small"
                                    placeholder="Search returns..."
                                    variant="outlined"
                                    sx={{ width: 180 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    disabled={loading}
                                />
                                <Tooltip title="Refresh" placement='top' arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={handleRefresh}
                                            disabled={refreshing || loading}
                                        >
                                            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {hasActiveFilters && (
                                    <Tooltip title="Clear Filters" placement='top' arrow>
                                        <IconButton
                                            size="small"
                                            onClick={handleClearFilters}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Accordion */}
                        <Accordion sx={{ my: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <AccordionSummary
                                expandIcon={<FilterListAltIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                                sx={{
                                    '&:hover': { backgroundColor: '#e9eef6' }
                                }}
                            >
                                Filters
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Compact Filter Section */}
                                <Paper elevation={0} sx={{
                                    p: 1.5,
                                    mb: 2,
                                    backgroundColor: 'background.default',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                        {/* Status Filter */}
                                        <FormControl size="small" sx={{ minWidth: '15%' }}>
                                            <Select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                displayEmpty
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <FilterIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Status</MenuItem>
                                                {STATUS_OPTIONS.map((status) => (
                                                    <MenuItem key={status} value={status}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <span>{STATUS_CONFIG[status].icon}</span>
                                                            {STATUS_CONFIG[status].label}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {/* Party Filter */}
                                        <Autocomplete
                                            size="small"
                                            options={parties}
                                            getOptionLabel={(option) => option.partyName || ''}
                                            value={partyFilter}
                                            onChange={(_, newValue) => setPartyFilter(newValue)}
                                            sx={{ width: '20%' }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Party"
                                                    disabled={loading}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <BusinessIcon fontSize="small" color="action" />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />

                                        {/* Vendor Filter */}
                                        <Autocomplete
                                            size="small"
                                            options={vendors}
                                            getOptionLabel={(option) => option.vendorName || ''}
                                            value={vendorFilter}
                                            onChange={(_, newValue) => setVendorFilter(newValue)}
                                            sx={{ width: '20%' }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Vendor"
                                                    disabled={loading}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <BusinessIcon fontSize="small" color="action" />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />

                                        {/* Quick Date Filters */}
                                        <FormControl size="small" sx={{ minWidth: '15%' }}>
                                            <Select
                                                value={quickDateFilter}
                                                onChange={(e) => setQuickDateFilter(e.target.value)}
                                                displayEmpty
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <CalendarIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Dates</MenuItem>
                                                <MenuItem value="today">Today</MenuItem>
                                                <MenuItem value="yesterday">Yesterday</MenuItem>
                                                <MenuItem value="this_week">Last 7 Days</MenuItem>
                                                <MenuItem value="this_month">Last 30 Days</MenuItem>
                                                <MenuItem value="last_month">Last Month</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Date Range Pickers */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '25%' }}>
                                            <DatePicker
                                                label="From"
                                                value={dateRange.start}
                                                onChange={(date) => {
                                                    setDateRange(prev => ({
                                                        ...prev,
                                                        start: date ? new Date(date as any) : null
                                                    }));
                                                    setQuickDateFilter('ALL');
                                                }}
                                                disabled={loading}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: { width: '100%' }
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2">-</Typography>
                                            <DatePicker
                                                label="To"
                                                value={dateRange.end}
                                                onChange={(date) => {
                                                    setDateRange(prev => ({
                                                        ...prev,
                                                        end: date ? new Date(date as any) : null
                                                    }));
                                                    setQuickDateFilter('ALL');
                                                }}
                                                disabled={loading}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: { width: '100%' }
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Paper>
                            </AccordionDetails>
                        </Accordion>

                        {/* Table */}
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {headCells.map((head) => (
                                            <TableCell
                                                key={head.id}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                    width: head.width,
                                                    py: 0.5
                                                }}
                                            >
                                                {head.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === head.id}
                                                        direction={orderBy === head.id ? order : 'asc'}
                                                        onClick={() => handleSort(head.id)}
                                                        sx={{ fontSize: '0.8rem' }}
                                                        disabled={loading}
                                                    >
                                                        {head.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    <Typography variant="caption" fontWeight="bold">
                                                        {head.label}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center" sx={{ py: 0 }}>
                                                <CircularProgress size={24} />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    Loading product returns...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : productReturns.length > 0 ? (
                                        productReturns.map((pr) => (
                                            <TableRow key={pr._id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                                                {/* Return Number */}
                                                <TableCell sx={{ py: 0.5, minWidth: 90 }}>
                                                    <Typography variant="body2" fontWeight="bold" fontSize="0.8rem">
                                                        {pr.productReturnNumber || '-'}
                                                    </Typography>
                                                </TableCell>

                                                {/* Return Date */}
                                                <TableCell sx={{ py: 0.5, minWidth: 90 }}>
                                                    <Typography variant="body2" fontSize="0.8rem">
                                                        {formatDate(pr.productReturnDate)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Items - Show item names with quantities */}
                                                <TableCell sx={{ py: 0.5, maxWidth: 200 }}>
                                                    {pr.items && pr.items.length > 0 ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            {pr.items.slice(0, 2).map((item, idx) => (
                                                                <Typography key={idx} variant="body2" fontSize="0.75rem" noWrap>
                                                                    • {item.itemName || 'Unknown'} {item.quantity ? `(Qty: ${item.quantity})` : ''}
                                                                </Typography>
                                                            ))}
                                                            {pr.items.length > 2 && (
                                                                <Tooltip title={
                                                                    <Box>
                                                                        {pr.items.slice(2).map((item, idx) => (
                                                                            <div key={idx}>
                                                                                • {item.itemName || 'Unknown'} {item.quantity ? `(Qty: ${item.quantity})` : ''}
                                                                            </div>
                                                                        ))}
                                                                    </Box>
                                                                } arrow>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }}>
                                                                        +{pr.items.length - 2} more items
                                                                    </Typography>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                                                            No items
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* From Party */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="body2" noWrap fontSize="0.8rem">
                                                            {getPartyName(pr.party)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* To Vendor */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="body2" noWrap fontSize="0.8rem">
                                                            {getVendorName(pr.vendor)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Status */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Chip
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <span>{getStatusIconBall(pr.status)}</span>
                                                                <span>{STATUS_CONFIG[pr.status as Status]?.label || pr.status}</span>
                                                            </Box>
                                                        }
                                                        color={getStatusColor(pr.status) as any}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: 24,
                                                            '& .MuiChip-label': { px: 1 }
                                                        }}
                                                    />
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell sx={{ py: 0, minWidth: 240 }} >
                                                    <Box display="flex" flexDirection="row" alignItems="center" gap={0.5}>
                                                        <Button
                                                            variant="text"
                                                            size="small"
                                                            onClick={() => navigate(`/productReturn/${pr._id}`)}
                                                            sx={{
                                                                minWidth: 'auto',
                                                                fontSize: '0.60rem',
                                                                textWrap: 'nowrap',
                                                                py: 0.1,
                                                                px: 2,
                                                                borderRadius: 5,
                                                                border: '.8px solid #7f5ee666',
                                                                '&:hover': { backgroundColor: '#7f5ee666' }
                                                            }}
                                                        >
                                                            View / Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            color="error"
                                                            onClick={() => handleDeleteClick(pr)}
                                                            sx={{
                                                                minWidth: 'auto',
                                                                fontSize: '0.60rem',
                                                                py: 0.1,
                                                                px: 2,
                                                                borderRadius: 5,
                                                                border: '.8px solid #ff4d6e5e',
                                                                '&:hover': { backgroundColor: '#ff4d6e5e' }
                                                            }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {hasActiveFilters
                                                        ? 'No product returns match your filters'
                                                        : 'No product returns found. Create your first product return!'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                '.MuiTablePagination-toolbar': {
                                    minHeight: 52,
                                    padding: 0,
                                },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    fontSize: '0.8rem',
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </LocalizationProvider>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={openDeleteDialog}
                loading={loadingDelete}
                title="Delete Product Return"
                description={
                    <>
                        Are you sure you want to delete the product return{' '}
                        <strong>"{itemToDelete?.productReturnNumber}"</strong>?
                        <br /><br />
                        <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
                            This action cannot be undone. All associated data will be permanently removed.
                        </Alert>
                    </>
                }
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export default ListProductReturn;